// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type CategoryPickerOption = { _id: string; label: string };

export type CategoryPickerPopupDetails = {
    options: CategoryPickerOption[];
    selected: string[];
    onConfirm: (selected: string[]) => void;
};

type Props = {
    onClose: () => void;
    details?: CategoryPickerPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * A product belongs to any number of categories, which a grid cell cannot show. A native multi-select
 * is unusable on a phone, so the cell opens this instead: plain checkboxes, big enough for a thumb.
 */
export default function CategoryPickerPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const [selected, setSelected] = useState<string[]>(details?.selected ?? []);

    if (!details) return null;

    const toggle = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

    const handleConfirm = () => {
        details.onConfirm(selected);
        onClose();
    };

    return (
        <div className="cat-picker">
            <p className="cat-picker-title">{t.menuEditor.categoriesOf}</p>

            <div className="cat-picker-list">
                {details.options.length === 0 && <p className="cat-picker-empty">{t.menuEditor.noCategoriesYet}</p>}
                {details.options.map((option) => (
                    <button
                        key={option._id}
                        type="button"
                        className={`cat-picker-item${selected.includes(option._id) ? " cat-picker-item--on" : ""}`}
                        onClick={() => toggle(option._id)}
                    >
                        <span className="cat-picker-box">
                            {selected.includes(option._id) && <img src="/icons/check-icon.svg" alt="" width={12} height={12} />}
                        </span>
                        <span className="cat-picker-label">{option.label}</span>
                    </button>
                ))}
            </div>

            <div className="cat-picker-actions">
                <button type="button" className="cat-picker-cancel" onClick={onClose}>
                    {t.menuEditor.cancel}
                </button>
                <button type="button" className="cat-picker-confirm" onClick={handleConfirm} disabled={selected.length === 0}>
                    {t.menuEditor.done}
                </button>
            </div>
        </div>
    );
}
